import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

/**
 * The URL search of the tab a pane is mounted in. The panes do not own
 * their Health & readiness route (T-0242 mounts them under a tab), so they
 * read the search loosely and only ever merge their own keys into it: any
 * other key the route keeps is left as it is.
 */
export function usePaneSearch(): {
  search: Record<string, unknown>;
  stringParam: (key: string) => string;
  update: (patch: Record<string, string | undefined>, opts?: { replace?: boolean }) => void;
} {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const update = useCallback(
    (patch: Record<string, string | undefined>, opts: { replace?: boolean } = {}) => {
      void navigate({
        to: '.',
        replace: opts.replace ?? false,
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev };
          for (const [key, value] of Object.entries(patch)) {
            // An emptied filter leaves the link, not `?severity=`.
            if (value === undefined || value === '') delete next[key];
            else next[key] = value;
          }
          return next;
        },
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );
  const stringParam = (key: string) => {
    const v = search[key];
    return typeof v === 'string' ? v : '';
  };
  return { search, stringParam, update };
}
